import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/f0-react'
import { StatusVariant } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Widget } from '@factorialco/f0-react/dist/experimental'
import { ArrowCycle, Delete } from '@factorialco/f0-react/icons/app'
import {
  BulkPaymentDetailPageDocument,
  BulkPaymentTransferFieldsFragment,
  BulkPaymentTransferStatsDocument,
  BulkPaymentTransfersDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { ConfirmRetryBulkPaymentModal } from 'modules/banking/components/ConfirmRetryBulkPaymentModal'
import { useMarkAsPaidPaymentExecution } from 'modules/banking/hooks/useMarkAsPaidPaymentExecution'
import { usePayBankingPayment } from 'modules/banking/hooks/usePayBankingPayment'
import { useRemoveBulkPayment } from 'modules/banking/hooks/useRemoveBulkPayment'
import { useRetryBankingPayment } from 'modules/banking/hooks/useRetryBankingPayment'
import { useUpdateSepaFile } from 'modules/banking/hooks/useUpdateSepaFile'
import { statusLabelType, statusText } from 'modules/banking/lib/mappers'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useResourceHeader } from 'modules/layout/components/ResponsivePage/hooks'

import { ConfirmRemoveTransferFromPaymentDialog } from './ConfirmRemoveTransferFromPaymentDialog'
import { ExecutionsTimeline } from './ExecutionsTimeline'
import { TransferData, TransferDetailSidePanel } from './TransferDetailSidePanel'
import { useRemoveTransferFromPayment } from './useRemoveTransferFromPayment'

interface InsightCardProps {
  title: string
  amount: number
  currency: string
  locale: string
  count: number
  statusVariant: StatusVariant
}

const InsightCard = ({
  title,
  amount,
  currency,
  locale,
  count,
  statusVariant,
}: InsightCardProps) => {
  const formattedAmount = MoneyWithCurrency({ amount, currency, locale })

  return (
    <Widget
      header={{ title }}
      status={{ text: `${count} transfers`, variant: statusVariant }}
      summaries={[{ label: i18n.t('banking.treasury.insights.amount'), value: formattedAmount }]}
    >
      <></>
    </Widget>
  )
}

const BulkPaymentDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const {
    employee,
    access: { locale },
  } = useCurrentSession()

  const [selectedTransfer, setSelectedTransfer] = useState<TransferData | null>(null)
  const [showRetryModal, setShowRetryModal] = useState(false)

  // Hook for removing transfers (payment requests) from the bulk payment
  const { handleRemoveTransfer, removeConfirmation, confirmRemove, cancelRemove, isRemoving } =
    useRemoveTransferFromPayment()

  // Hook to update header dynamically
  const { updateHeader } = useResourceHeader()

  // Hooks for header actions
  const { payPayment } = usePayBankingPayment()
  const { updateSepaFile, processDownload } = useUpdateSepaFile()
  const { markAsPaidPaymentExecution } = useMarkAsPaidPaymentExecution()
  const { submitRemoveBulkPayment } = useRemoveBulkPayment()
  const { retryPayment } = useRetryBankingPayment({
    refetchQueries: [BulkPaymentDetailPageDocument, BulkPaymentTransferStatsDocument],
  })

  // Query for payment executions (for timeline and actions)
  const { data, loading, error } = useQuery(BulkPaymentDetailPageDocument, {
    variables: { id: String(id) },
    skip: !id,
  })

  const payment = data?.banking?.payment
  const currency = payment?.currency ?? 'EUR'

  // Get execution IDs for filtering transfers - keep last valid value to prevent empty queries
  const paymentExecutionIdsRef = useRef<string[]>([])
  const paymentExecutionIds = useMemo(() => {
    if (payment?.paymentExecutions && payment.paymentExecutions.length > 0) {
      const ids = payment.paymentExecutions.map((execution) => execution.id)
      paymentExecutionIdsRef.current = ids
      return ids
    }
    return paymentExecutionIdsRef.current
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(payment?.paymentExecutions?.map((e) => e.id))])

  // Query for transfer stats (aggregates)
  const { data: statsData } = useQuery(BulkPaymentTransferStatsDocument, {
    variables: { paymentExecutionIds },
    skip: paymentExecutionIds.length === 0,
    fetchPolicy: 'no-cache',
  })

  // Build executions for timeline
  const executions = useMemo(() => {
    if (!payment?.paymentExecutions) return []

    return payment.paymentExecutions.map((execution) => ({
      id: execution.id,
      status: execution.status,
      date: execution.date,
      executedAt: execution.executedAt,
      paidAt: execution.paidAt,
      transfersCount: execution.transfersConnection?.totalCount ?? 0,
    }))
  }, [payment])

  // Stats from aggregates
  const totalCount = statsData?.banking?.total?.aggregate?.count ?? 0
  const totalAmount = statsData?.banking?.total?.aggregate?.sum?.amountCents ?? 0
  const paidCount = statsData?.banking?.paid?.aggregate?.count ?? 0
  const paidAmount = statsData?.banking?.paid?.aggregate?.sum?.amountCents ?? 0
  const inProgressCount = statsData?.banking?.inProgress?.aggregate?.count ?? 0
  const inProgressAmount = statsData?.banking?.inProgress?.aggregate?.sum?.amountCents ?? 0
  const rejectedCount = statsData?.banking?.rejected?.aggregate?.count ?? 0
  const rejectedAmount = statsData?.banking?.rejected?.aggregate?.sum?.amountCents ?? 0

  // Get last payment execution for actions
  const lastPaymentExecution = payment?.paymentExecutions?.[payment.paymentExecutions.length - 1]

  // Update header with retry action when there are rejected transfers
  useUnsafeEffect(() => {
    if (
      rejectedCount > 0 &&
      lastPaymentExecution?.status !== 'in_progress' &&
      lastPaymentExecution?.status !== 'pending_consent'
    ) {
      updateHeader((prev) => ({
        ...prev,
        secondaryActions: [
          {
            label: i18n.t('banking.payments.retry_bulk_payment'),
            icon: ArrowCycle,
            key: 'retry_bulk_payment',
          },
          ...(prev?.secondaryActions ?? []),
        ],
      }))
    } else {
      // Reset header override when no rejected transfers
      updateHeader(null)
    }
  }, [rejectedCount, updateHeader, lastPaymentExecution?.status])

  const refs = useRef({
    id,
    employee,
    lastPaymentExecution,
    rejectedCount,
    payPayment,
    processDownload,
    updateSepaFile,
    markAsPaidPaymentExecution,
    submitRemoveBulkPayment,
    navigate,
    navigation,
    retryPayment,
    setShowRetryModal,
  })
  // Update refs on every render
  refs.current = {
    id,
    employee,
    lastPaymentExecution,
    rejectedCount,
    payPayment,
    processDownload,
    updateSepaFile,
    markAsPaidPaymentExecution,
    submitRemoveBulkPayment,
    navigate,
    navigation,
    retryPayment,
    setShowRetryModal,
  }

  // Header action click handler - completely stable callback (empty deps)
  const handleActionClick = useCallback(async (key: string) => {
    const {
      id: currentId,
      employee: currentEmployee,
      lastPaymentExecution: currentExecution,
      rejectedCount: currentRejectedCount,
      payPayment: doPayPayment,
      processDownload: doProcessDownload,
      updateSepaFile: doUpdateSepaFile,
      markAsPaidPaymentExecution: doMarkAsPaid,
      submitRemoveBulkPayment: doRemove,
      navigate: doNavigate,
      navigation: nav,
      retryPayment: doRetry,
      setShowRetryModal: doSetShowRetryModal,
    } = refs.current

    if (!currentId) return

    switch (key) {
      case 'pay_with_sepa_transfer': {
        const employeeId = currentEmployee?.id
        if (!employeeId) return
        const redirectUrl = window.location.href
        const result = await doPayPayment(currentId, employeeId, redirectUrl)
        if (result.authorizationUrl) {
          window.location.href = result.authorizationUrl
        } else if (result.success && result.consentUrl) {
          window.location.href = result.consentUrl
        }
        break
      }
      case 'download_sepa_file': {
        if (currentExecution?.sepaFile?.url) {
          doProcessDownload(currentExecution.sepaFile.url, currentExecution.sepaFile.filename)
        } else if (currentExecution) {
          doUpdateSepaFile(currentExecution.id)
        }
        break
      }
      case 'mark_as_paid': {
        if (currentExecution) {
          doMarkAsPaid(currentExecution.id)
        }
        break
      }
      case 'delete_bulk_payment': {
        const result = await doRemove({ paymentId: currentId })
        if (result?.success) {
          doNavigate(nav.finance.treasury.banking.payments.fullPath())
        }
        break
      }
      case 'continue_consent': {
        const consentUrl = currentExecution?.consent?.consentUrl
        if (consentUrl) {
          window.location.href = consentUrl
        }
        break
      }
      case 'retry_bulk_payment': {
        // If only 1 rejected transfer, retry directly without modal
        const employeeIdForRetry = currentEmployee?.id
        if (!employeeIdForRetry) return
        if (currentRejectedCount === 1) {
          const result = await doRetry({
            paymentId: currentId,
            employeeId: employeeIdForRetry,
            redirectUrl: window.location.href,
          })
          if (result.consentUrl) {
            window.location.href = result.consentUrl
          } else if (result.authorizationUrl) {
            window.location.href = result.authorizationUrl
          }
        } else {
          doSetShowRetryModal(true)
        }
        break
      }
    }
  }, [])

  // Row actions for transfers table — only show remove action for PR-linked transfers on init payments
  const getItemActions = useCallback(
    (transfer: BulkPaymentTransferFieldsFragment) => {
      if (!transfer.paymentRequestItem || payment?.status !== 'init') return []
      return [
        {
          id: 'remove_from_payment',
          label: i18n.t('banking.bulkPayments.detail.removePaymentRequest'),
          icon: Delete,
          critical: true,
          onClick: () => {
            handleRemoveTransfer(transfer, id ?? '', paymentExecutionIds)
          },
        },
      ]
    },
    [payment?.status, id, paymentExecutionIds, handleRemoveTransfer]
  )

  // DataCollection source for transfers table (paginated)
  const source = useGraphqlDataCollectionSource(
    {
      document: BulkPaymentTransfersDataCollectionDocument,
      mapData: (data) =>
        data.banking.transfersConnection.edges.map(
          (edge: { node: BulkPaymentTransferFieldsFragment }) => ({
            ...edge.node,
            paymentRequestLabel: edge.node.paymentRequestItem?.paymentRequest?.label ?? '-',
          })
        ),
      mapVariables: ({ sortings }) => ({
        paymentExecutionIds,
        sortOrder: sortings?.[0],
      }),
      paginationType: 'pages',
      perPage: 10,
      mapPagination: (data) => ({
        total: data.banking.transfersConnection.totalCount ?? 0,
        hasNextPage: data.banking.transfersConnection.pageInfo.hasNextPage ?? false,
      }),
      itemActions: getItemActions,
      itemOnClick: (item: BulkPaymentTransferFieldsFragment) => () => {
        setSelectedTransfer({
          id: item.id,
          accountNumber: item.accountNumber ?? '',
          amountCents: item.amountCents,
          beneficiaryName: item.beneficiaryName ?? '-',
          createdAt: item.createdAt,
          currency: item.currency,
          dueDate: item.dueDate,
          label: item.paymentRequestItem?.paymentRequest?.label ?? '',
          paymentRequestItem: item.paymentRequestItem,
          rejectionReason: item.rejectionReason,
          status: item.status,
        })
      },
    },
    [paymentExecutionIds, getItemActions]
  )

  // Columns for table
  const columns = useMemo(
    () => [
      {
        label: i18n.t('banking.bulkPayments.detail.columns.beneficiary'),
        render: (row: BulkPaymentTransferFieldsFragment) => ({
          type: 'text' as const,
          value: row.beneficiaryName ?? '-',
        }),
      },
      {
        label: i18n.t('banking.bulkPayments.detail.columns.amount'),
        render: (row: BulkPaymentTransferFieldsFragment) => ({
          type: 'text' as const,
          value: MoneyWithCurrency({ amount: row.amountCents, currency: row.currency, locale }),
        }),
      },
      {
        label: i18n.t('banking.bulkPayments.detail.columns.dueDate'),
        render: (row: BulkPaymentTransferFieldsFragment) =>
          row.dueDate
            ? convertDate(row.dueDate, 'backend_datetime', 'ui_abbreviated_month_year')
            : '-',
      },
      {
        label: i18n.t('banking.bulkPayments.detail.columns.status'),
        render: (row: BulkPaymentTransferFieldsFragment) => ({
          type: 'status' as const,
          value: {
            label: statusText(row.status),
            status: statusLabelType(row.status),
          },
        }),
      },
      {
        label: i18n.t('banking.bulkPayments.detail.columns.paymentRequest'),
        render: (row: BulkPaymentTransferFieldsFragment) => ({
          type: 'text' as const,
          value: row.paymentRequestItem?.paymentRequest?.label ?? '-',
        }),
      },
    ],
    [locale]
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns,
        },
      },
    ],
    [columns]
  )

  const sideContent = (
    <Box flexDirection="column" height="full">
      <Box padding="s16" style={{ borderBottom: '1px solid var(--grey200)' }}>
        <Text.Normal weight="semibold" size="200" color="grey700">
          {i18n.t('banking.bulkPayments.detail.executionsTitle').toUpperCase()}
        </Text.Normal>
      </Box>
      <Box padding="s16" style={{ overflow: 'auto', flex: '1' }}>
        <ExecutionsTimeline executions={executions} />
      </Box>
    </Box>
  )

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      error={error}
      loading={loading}
      onActionClick={handleActionClick}
    >
      <TwoColumnLayout sideContent={sideContent}>
        <Box flexDirection="column" gap="s24" height="full">
          {/* Insight Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InsightCard
              title={i18n.t('banking.treasury.insights.total')}
              amount={totalAmount}
              currency={currency}
              locale={locale}
              count={totalCount}
              statusVariant="neutral"
            />
            <InsightCard
              title={i18n.t('banking.treasury.insights.paid')}
              amount={paidAmount}
              currency={currency}
              locale={locale}
              count={paidCount}
              statusVariant="positive"
            />
            <InsightCard
              title={i18n.t('banking.treasury.insights.inProgress')}
              amount={inProgressAmount}
              currency={currency}
              locale={locale}
              count={inProgressCount}
              statusVariant="info"
            />
            <InsightCard
              title={i18n.t('banking.treasury.insights.pendingRetry')}
              amount={rejectedAmount}
              currency={currency}
              locale={locale}
              count={rejectedCount}
              statusVariant="critical"
            />
          </div>

          {/* Transfers Table */}
          <Box height="full">
            {paymentExecutionIds.length > 0 && (
              <OneDataCollection source={source} visualizations={visualizations} fullHeight />
            )}
          </Box>
        </Box>
      </TwoColumnLayout>

      {selectedTransfer && (
        <TransferDetailSidePanel
          transfer={selectedTransfer}
          onClose={() => setSelectedTransfer(null)}
        />
      )}

      {showRetryModal && id && employee && (
        <ConfirmRetryBulkPaymentModal
          paymentId={id}
          payerId={employee.id}
          paymentExecutionIds={paymentExecutionIds}
          locale={locale}
          onClose={() => setShowRetryModal(false)}
          onSuccess={() => {
            // Data is automatically refetched by the mutation's refetchQueries
          }}
        />
      )}

      {removeConfirmation && (
        <ConfirmRemoveTransferFromPaymentDialog
          data={removeConfirmation}
          locale={locale}
          isLoading={isRemoving}
          onConfirm={confirmRemove}
          onClose={cancelRemove}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default BulkPaymentDetailPage
